import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import "./modal.css";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";

interface IModalProps {
  UI: ReactNode;
  onClose: () => void;
  fullScreen?: boolean;
  modalBoxClassName?: string;
  closeOnBackdropClick?: boolean;
  title?: string;
}

const CLOSE_MS = 240;
const DRAG_KEEP_X = 120;
const DRAG_KEEP_Y = 48;

type DragSession = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

type ModalSlot = {
  id: number;
  signature: string;
  node: ReactNode;
  title?: string;
  fullScreen?: boolean;
  modalBoxClassName?: string;
  closeOnBackdropClick?: boolean;
  live: boolean;
  minimized: boolean;
};

type ParkedModal = {
  id: number;
  node: ReactNode;
  title?: string;
  fullScreen?: boolean;
  modalBoxClassName?: string;
  minimized: boolean;
};

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const dockSlots = new Set<number>();

function claimDockSlot() {
  let index = 0;
  while (dockSlots.has(index)) index += 1;
  dockSlots.add(index);
  return index;
}

let slotSequence = 0;

function nextSlotId() {
  slotSequence += 1;
  return slotSequence;
}

const IDENTITY_PROP =
  /(^id$|Id$|ID$|tab$|mode$|kind$|type$|^name$|^key$|^headerName$)/;

function typeName(type: unknown): string {
  if (typeof type === "string") return type;
  if (typeof type === "function") {
    const fn = type as { displayName?: string; name?: string };
    return fn.displayName || fn.name || "fn";
  }
  if (type && typeof type === "object") {
    const obj = type as {
      displayName?: string;
      type?: unknown;
      render?: { name?: string };
    };
    if (obj.displayName) return obj.displayName;
    if (obj.type) return typeName(obj.type);
    if (obj.render?.name) return obj.render.name;
  }
  return "node";
}

function identityValue(value: unknown): string {
  if (value == null) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (Array.isArray(value)) return value.slice(0, 6).map(identityValue).join("|");
  if (typeof value === "object" && "id" in value) {
    const id = (value as { id?: unknown }).id;
    if (typeof id === "string" || typeof id === "number") return String(id);
  }
  return "";
}

function reactChildNodes(children: unknown): ReactNode[] {
  if (children == null || typeof children === "boolean") return [];
  if (
    typeof children === "string" ||
    typeof children === "number" ||
    React.isValidElement(children)
  ) {
    return [children];
  }
  if (Array.isArray(children)) {
    return children.flatMap((child) => reactChildNodes(child));
  }
  return [];
}

function dialogSignature(node: ReactNode, depth = 0): string {
  if (!React.isValidElement(node)) return "";
  const props = (node.props ?? {}) as Record<string, unknown>;
  const bits = Object.keys(props)
    .filter((key) => key !== "children" && IDENTITY_PROP.test(key))
    .sort()
    .map((key) => `${key}=${identityValue(props[key])}`)
    .join(",");
  if (depth >= 3) return `${typeName(node.type)}{${bits}}`;
  // Some components (TabBox) store data objects on `children`. React.Children.toArray
  // throws when those objects are not renderable nodes.
  const children = reactChildNodes(props.children)
    .map((child) => dialogSignature(child, depth + 1))
    .filter(Boolean)
    .join("+");
  return `${typeName(node.type)}{${bits}}${children ? `(${children})` : ""}`;
}

function detachClose(
  node: ReactNode,
  dismiss: () => void,
): ReactNode {
  if (!React.isValidElement(node)) return node;
  const props = node.props as { onClose?: unknown };
  if (typeof props.onClose !== "function") return node;
  return React.cloneElement(node as React.ReactElement<{ onClose?: () => void }>, {
    onClose: dismiss,
  });
}

function flyTo(
  el: HTMLElement,
  from: DOMRect,
  minimized: boolean,
  offset: { x: number; y: number },
) {
  const to = el.getBoundingClientRect();
  const sx = from.width / Math.max(to.width, 1);
  const sy = from.height / Math.max(to.height, 1);
  const tx = from.left - to.left;
  const ty = from.top - to.top + to.height * (sy - 1);
  el.style.transition = "none";
  el.style.transformOrigin = "left bottom";
  el.style.transform = `translate(${tx}px, ${ty}px) scale(${sx}, ${sy})`;
  el.getBoundingClientRect();
  el.style.transition = "transform 340ms cubic-bezier(0.2, 0.8, 0.2, 1)";
  el.style.transform = minimized
    ? "none"
    : `translate(${offset.x}px, ${offset.y}px)`;

  const finish = (event: TransitionEvent) => {
    if (event.propertyName !== "transform") return;
    el.style.transition = "";
    el.style.transformOrigin = "";
    el.removeEventListener("transitionend", finish);
  };
  el.addEventListener("transitionend", finish);
}

let parked: ParkedModal[] = [];
const parkListeners = new Set<() => void>();

function emitParked() {
  parkListeners.forEach((listener) => listener());
}

function subscribeParked(listener: () => void) {
  parkListeners.add(listener);
  return () => parkListeners.delete(listener);
}

function getParkedSnapshot() {
  return parked;
}

function parkModals(items: ParkedModal[]) {
  if (!items.length) return;
  parked = [...parked, ...items];
  emitParked();
}

function patchParked(id: number, minimized: boolean) {
  const current = parked.find((item) => item.id === id);
  if (!current || current.minimized === minimized) return;
  const updated = { ...current, minimized };
  if (minimized) {
    parked = parked.map((item) => (item.id === id ? updated : item));
  } else {
    parked = [...parked.filter((item) => item.id !== id), updated];
  }
  emitParked();
}

function removeParked(id: number) {
  const next = parked.filter((item) => item.id !== id);
  if (next.length === parked.length) return;
  parked = next;
  emitParked();
}

type ShellProps = {
  UI: ReactNode;
  title?: string;
  fullScreen?: boolean;
  modalBoxClassName?: string;
  closeOnBackdropClick?: boolean;
  minimized: boolean;
  onMinimizedChange: (minimized: boolean) => void;
  onClose: () => void;
};

const ModalShell: React.FC<ShellProps> = ({
  UI,
  title,
  fullScreen,
  modalBoxClassName,
  closeOnBackdropClick,
  minimized,
  onMinimizedChange,
  onClose,
}) => {
  const [closing, setClosing] = useState(false);
  const [settled, setSettled] = useState(false);
  const [dockSlot, setDockSlot] = useState(0);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const closingRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const anchorRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(offset);
  const dragRef = useRef<DragSession | null>(null);
  const dockSlotRef = useRef<number | null>(null);
  const flyFromRef = useRef<DOMRect | null>(null);
  onCloseRef.current = onClose;
  offsetRef.current = offset;

  useEffect(
    () => () => {
      if (dockSlotRef.current != null) dockSlots.delete(dockSlotRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!minimized || dockSlotRef.current != null) return;
    const slot = claimDockSlot();
    dockSlotRef.current = slot;
    setDockSlot(slot);
  }, [minimized]);

  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(() => onCloseRef.current(), CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [closing]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(true), 400);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const from = flyFromRef.current;
    const el = anchorRef.current;
    flyFromRef.current = null;
    if (!from || !el || prefersReducedMotion()) return;
    flyTo(el, from, minimized, offsetRef.current);
  }, [minimized]);

  const requestClose = () => {
    if (closingRef.current) return;
    if (prefersReducedMotion()) {
      onCloseRef.current();
      return;
    }
    closingRef.current = true;
    setClosing(true);
  };

  const minimize = () => {
    if (minimized || closingRef.current) return;
    flyFromRef.current = anchorRef.current?.getBoundingClientRect() ?? null;
    if (dockSlotRef.current == null) {
      const slot = claimDockSlot();
      dockSlotRef.current = slot;
      setDockSlot(slot);
    }
    onMinimizedChange(true);
  };

  const restore = () => {
    if (!minimized || closingRef.current) return;
    flyFromRef.current = anchorRef.current?.getBoundingClientRect() ?? null;
    if (dockSlotRef.current != null) {
      dockSlots.delete(dockSlotRef.current);
      dockSlotRef.current = null;
    }
    onMinimizedChange(false);
  };

  const onDragPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || closingRef.current || minimized) return;
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const { x, y } = offsetRef.current;
    const baseLeft = rect.left - x;
    const baseTop = rect.top - y;
    const keepX = Math.min(DRAG_KEEP_X, rect.width);
    let minX = keepX - rect.width - baseLeft;
    let maxX = window.innerWidth - keepX - baseLeft;
    let minY = -baseTop;
    let maxY = window.innerHeight - DRAG_KEEP_Y - baseTop;
    if (minX > maxX) {
      const mid = (minX + maxX) / 2;
      minX = mid;
      maxX = mid;
    }
    if (minY > maxY) {
      const mid = (minY + maxY) / 2;
      minY = mid;
      maxY = mid;
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: x,
      originY: y,
      minX,
      maxX,
      minY,
      maxY,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const onDragPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const next = {
      x: Math.min(drag.maxX, Math.max(drag.minX, drag.originX + e.clientX - drag.startX)),
      y: Math.min(drag.maxY, Math.max(drag.minY, drag.originY + e.clientY - drag.startY)),
    };
    offsetRef.current = next;
    setOffset(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId !== e.pointerId) return;
    dragRef.current = null;
  };

  return createPortal(
    <div
      className={`modal-container${closing ? " is-closing" : ""}${
        minimized ? " is-minimized" : ""
      }`}
      onClick={(e) => {
        e.stopPropagation();
        if (closeOnBackdropClick && e.target === e.currentTarget) requestClose();
      }}
    >
      <div
        ref={anchorRef}
        className="modal-box-anchor"
        style={
          minimized
            ? { bottom: 16 + dockSlot * 56 }
            : { transform: `translate(${offset.x}px, ${offset.y}px)` }
        }
      >
        <div
          className={`modal-box${settled ? " modal-box-settled" : ""} ${
            fullScreen ? "h-screen w-screen" : ""
          } ${modalBoxClassName || ""}`}
        >
          <div
            className="modal-drag-handle"
            title="جابه‌جایی"
            onPointerDown={onDragPointerDown}
            onPointerMove={onDragPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />
          <button
            type="button"
            className="minimize-icon"
            title={minimized ? "باز کردن" : "کوچک کردن"}
            onClick={(e) => {
              e.stopPropagation();
              if (minimized) restore();
              else minimize();
            }}
          >
            {minimized ? <OpenInFullIcon fontSize="small" /> : <RemoveIcon fontSize="small" />}
          </button>
          <div
            className="close-icon"
            onClick={(e) => {
              e.stopPropagation();
              requestClose();
            }}
          >
            <CloseIcon />
          </div>
          {minimized ? (
            <button
              type="button"
              className="modal-dock-label"
              title={title || "بازگشت"}
              onClick={restore}
            >
              {title || "بازگشت"}
            </button>
          ) : null}
          <div className="modal-UI">{UI}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export function MinimizedModalHost() {
  const items = useSyncExternalStore(subscribeParked, getParkedSnapshot, getParkedSnapshot);
  const ordered = [
    ...items.filter((item) => item.minimized),
    ...items.filter((item) => !item.minimized),
  ];
  return (
    <>
      {ordered.map((item) => (
        <ModalShell
          key={item.id}
          UI={item.node}
          title={item.title}
          fullScreen={item.fullScreen}
          modalBoxClassName={item.modalBoxClassName}
          minimized={item.minimized}
          onMinimizedChange={(minimized) => patchParked(item.id, minimized)}
          onClose={() => removeParked(item.id)}
        />
      ))}
    </>
  );
}

const Modal: React.FC<IModalProps> = ({
  UI,
  onClose,
  fullScreen,
  modalBoxClassName,
  closeOnBackdropClick,
  title,
}) => {
  const signature = dialogSignature(UI);
  const [slots, setSlots] = useState<ModalSlot[]>(() => [
    {
      id: nextSlotId(),
      signature: dialogSignature(UI),
      node: UI,
      title,
      fullScreen,
      modalBoxClassName,
      closeOnBackdropClick,
      live: true,
      minimized: false,
    },
  ]);
  const liveNodeRef = useRef(UI);
  const slotsRef = useRef(slots);
  const closedIdsRef = useRef(new Set<number>());
  const dismissSlotRef = useRef<(id: number) => void>(() => {});
  slotsRef.current = slots;
  dismissSlotRef.current = (id: number) => {
    closedIdsRef.current.add(id);
    setSlots((prev) => prev.filter((slot) => slot.id !== id));
  };

  const live = slots.find((slot) => slot.live);

  if (live && live.minimized && live.signature !== signature) {
    const previousNode = liveNodeRef.current;
    const frozenId = live.id;
    setSlots((current) => {
      const currentLive = current.find((slot) => slot.live);
      if (!currentLive || !currentLive.minimized || currentLive.signature === signature) {
        return current;
      }
      const frozen: ModalSlot = {
        ...currentLive,
        live: false,
        minimized: true,
        node: detachClose(previousNode, () => dismissSlotRef.current(frozenId)),
      };
      const next: ModalSlot = {
        id: nextSlotId(),
        signature,
        node: UI,
        title,
        fullScreen,
        modalBoxClassName,
        closeOnBackdropClick,
        live: true,
        minimized: false,
      };
      return [...current.filter((slot) => slot.id !== currentLive.id), frozen, next];
    });
  } else if (
    live &&
    (live.signature !== signature ||
      live.title !== title ||
      live.fullScreen !== fullScreen ||
      live.modalBoxClassName !== modalBoxClassName ||
      live.closeOnBackdropClick !== closeOnBackdropClick)
  ) {
    setSlots((current) =>
      current.map((slot) =>
        slot.live
          ? {
              ...slot,
              signature,
              node: UI,
              title,
              fullScreen,
              modalBoxClassName,
              closeOnBackdropClick,
            }
          : slot,
      ),
    );
  }
  liveNodeRef.current = UI;

  useLayoutEffect(
    () => () => {
      const leftovers = slotsRef.current.filter(
        (slot) => !closedIdsRef.current.has(slot.id) && (slot.minimized || !slot.live),
      );
      const items = leftovers.map((slot) => ({
        id: slot.id,
        node: detachClose(slot.live ? liveNodeRef.current : slot.node, () =>
          removeParked(slot.id),
        ),
        title: slot.title,
        fullScreen: slot.fullScreen,
        modalBoxClassName: slot.modalBoxClassName,
        minimized: slot.minimized,
      }));
      queueMicrotask(() => parkModals(items));
    },
    [],
  );

  const setSlotMinimized = (id: number, minimized: boolean) => {
    setSlots((prev) => {
      const current = prev.find((slot) => slot.id === id);
      if (!current || current.minimized === minimized) return prev;
      const updated = { ...current, minimized };
      if (minimized) return prev.map((slot) => (slot.id === id ? updated : slot));
      return [...prev.filter((slot) => slot.id !== id), updated];
    });
  };

  const ordered = [
    ...slots.filter((slot) => slot.minimized),
    ...slots.filter((slot) => !slot.minimized),
  ];

  return (
    <>
      {ordered.map((slot) => (
        <ModalShell
          key={slot.id}
          UI={slot.live ? UI : slot.node}
          title={slot.live ? title : slot.title}
          fullScreen={slot.live ? fullScreen : slot.fullScreen}
          modalBoxClassName={slot.live ? modalBoxClassName : slot.modalBoxClassName}
          closeOnBackdropClick={slot.live ? closeOnBackdropClick : slot.closeOnBackdropClick}
          minimized={slot.minimized}
          onMinimizedChange={(minimized) => setSlotMinimized(slot.id, minimized)}
          onClose={() => {
            closedIdsRef.current.add(slot.id);
            if (slot.live) onClose();
            else dismissSlotRef.current(slot.id);
          }}
        />
      ))}
    </>
  );
};

export default Modal;
